// src/services/api.js
import { FUNCTION_URL, FUNCTION_KEY } from '../utils/constants';
import { processJsonResponse, processCsvResponse, processOtherResponse } from '../utils/responseHandlers';

function buildBearerHeaders(accessToken) {
  return {
    Authorization: `Bearer ${accessToken}`,
  };
}

function buildKeyHeaders(keyToken) {
  return {
    Key: keyToken,
  };
}

function addFunctionKeyToUrl(url) {
  if (FUNCTION_KEY) {
    url.searchParams.set('code', FUNCTION_KEY);
  }
  return url;
}

// Helper to detect if server response should be treated as JSON
function isJsonLike(contentType, bodyText) {
  const ct = (contentType || '').toLowerCase();
  if (ct.includes('json')) return true;
  const trimmed = (bodyText || '').trim();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

// Helper to force a browser download from processedResult
function triggerDownload(processedResult, getDynamicFilename, { resolvedFormat, selectedYear, fallbackExt }) {
  if (processedResult?.noDataToDownload) {
    return { noData: true };
  }
  const blob = new Blob([processedResult.processedDataForBlob], {
    type: processedResult.finalBlobType || (resolvedFormat === 'json' ? 'application/json;charset=utf-8' : 'text/plain;charset=utf-8'),
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const ext = resolvedFormat === 'json' ? 'json' : resolvedFormat === 'csv' ? 'csv' : (fallbackExt || 'dat');
  a.href = url;
  a.download = getDynamicFilename?.({ dataFormat: resolvedFormat, selectedYear }) || `download.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { success: true };
}

/**
 * Accepts the DSA for the current user.
 * 
 * @param {Object} params
 * @param {Function} params.getAccessTokenSilently - Auth0 token getter
 * @param {string} params.userEmail - User's email
 * @param {string} params.userName - User's full name
 * @param {string} params.userRole - User's role designation
 * @returns {Promise<Object>} Response from the server
 */
export const acceptDsa = async ({ getAccessTokenSilently, userEmail, userName, userRole }) => {
  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error('getAccessTokenSilently is not a function. Please check Auth0 integration.');
  }

  try {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
    });

    // Build URL with email parameter
    const url = new URL(`${FUNCTION_URL}/api/v1/users/dsa-accept`);
    url.searchParams.set('email', userEmail);
    addFunctionKeyToUrl(url);

    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        ...buildBearerHeaders(accessToken),
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`Error accepting DSA: ${res.status} ${txt?.slice(0, 200)}`);
    }
    return await res.json();
  } catch (error) {
    console.error('Error in acceptDsa:', error);
    throw error;
  }
};

export const rejectDsa = async ({ getAccessTokenSilently, userEmail }) => {
  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error('getAccessTokenSilently is not a function. Please check Auth0 integration.');
  }

  if (!userEmail) {
    throw new Error('User email is required');
  }

  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  const url = new URL(`${FUNCTION_URL}/api/v1/users/dsa-reject`);
  url.searchParams.set('email', userEmail);
  addFunctionKeyToUrl(url);

  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      ...buildBearerHeaders(accessToken),
      'Content-Type': 'application/json',
    },
  });

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Error rejecting DSA: ${res.status} ${txt?.slice(0, 200)}`);
  }
  return true;
};

export const fetchData = async ({
  getAccessTokenSilently,
  selectedEndpoint,
  dataFormat,
  selectedYear,
  getDynamicFilename
}) => {
  if (!selectedEndpoint || !selectedEndpoint.path) {
    throw new Error('Invalid endpoint configuration.');
  }

  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error('getAccessTokenSilently is not a function. Please check Auth0 integration.');
  }

  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  const method = (selectedEndpoint.method || 'GET').toUpperCase();

  const yearParam = selectedEndpoint.parameters?.year;
  const formatParam = selectedEndpoint.parameters?.format;

  const yearAllowed = !!yearParam;
  const formatAllowed = !!formatParam;

  let resolvedFormat = dataFormat ? String(dataFormat).toLowerCase() : undefined;
  if (!resolvedFormat && formatAllowed) {
    const allowedValues = Array.isArray(formatParam.values) ? formatParam.values : [];
    if (allowedValues.length > 0) {
      resolvedFormat = String(allowedValues[0]).toLowerCase();
    }
  }
  if (!resolvedFormat) {
    resolvedFormat = 'csv';
  }

  const apiUrl = new URL(`${FUNCTION_URL}/api/${selectedEndpoint.path}`);
  
  if (formatAllowed && resolvedFormat) {
    apiUrl.searchParams.set('format', resolvedFormat);
  }
  if (yearAllowed && selectedYear && selectedYear !== 'all') {
    apiUrl.searchParams.set('year', String(selectedYear));
  }
  
  addFunctionKeyToUrl(apiUrl);

  const keyHeaders = buildKeyHeaders(accessToken);

  if (method === 'GET') {
    const response = await fetch(apiUrl.toString(), {
      method: 'GET',
      headers: keyHeaders,
    });

    const responseDataText = await response.text();

    if (!response.ok) {
      let errorDetailMessage = '';
      try {
        const errorJson = JSON.parse(responseDataText);
        errorDetailMessage =
          errorJson.error ||
          errorJson.message ||
          errorJson.detail ||
          responseDataText.substring(0, 200);
      } catch {
        errorDetailMessage = responseDataText.substring(0, 200);
      }
      throw new Error(`Error from server: ${response.status} - ${errorDetailMessage}`);
    }

    const contentTypeFromServer = response.headers.get('content-type');
    const ctLower = (contentTypeFromServer || '').toLowerCase();

    let processedResult;

    if (isJsonLike(contentTypeFromServer, responseDataText)) {
      processedResult = processJsonResponse(responseDataText);
      return triggerDownload(processedResult, getDynamicFilename, { resolvedFormat: 'json', selectedYear, fallbackExt: 'json' });
    }

    if (ctLower.includes('text/csv')) {
      processedResult = processCsvResponse(responseDataText, selectedEndpoint.path);
      return triggerDownload(processedResult, getDynamicFilename, { resolvedFormat: 'csv', selectedYear, fallbackExt: 'csv' });
    }

    processedResult = processOtherResponse(responseDataText, contentTypeFromServer);
    return triggerDownload(processedResult, getDynamicFilename, { resolvedFormat, selectedYear });
  }

  const response = await fetch(apiUrl.toString(), {
    method,
    headers: {
      ...keyHeaders,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  const responseDataText = await response.text();

  if (!response.ok) {
    let errorDetailMessage = '';
    try {
      const errorJson = JSON.parse(responseDataText);
      errorDetailMessage =
        errorJson.error ||
        errorJson.message ||
        errorJson.detail ||
        responseDataText.substring(0, 200);
    } catch {
      errorDetailMessage = responseDataText.substring(0, 200);
    }
    throw new Error(`Error from server: ${response.status} - ${errorDetailMessage}`);
  }

  const contentTypeFromServer = response.headers.get('content-type') || '';
  const ctLower = contentTypeFromServer.toLowerCase();

  if (isJsonLike(contentTypeFromServer, responseDataText)) {
    const processedResult = processJsonResponse(responseDataText);
    return triggerDownload(processedResult, getDynamicFilename, { resolvedFormat: 'json', selectedYear, fallbackExt: 'json' });
  }

  if (ctLower.includes('text/csv') || ctLower.includes('application/octet-stream')) {
    const processedResult = ctLower.includes('text/csv')
      ? processCsvResponse(responseDataText, selectedEndpoint.path)
      : processOtherResponse(responseDataText, contentTypeFromServer);

    return triggerDownload(processedResult, getDynamicFilename, { resolvedFormat, selectedYear });
  }

  const processedResult = processOtherResponse(responseDataText, contentTypeFromServer);
  return triggerDownload(processedResult, getDynamicFilename, { resolvedFormat, selectedYear });
};

/**
 * Fetches aggregate mission count data for a given year.
 * 
 * @param {Object} params
 * @param {Function} params.getAccessTokenSilently - Auth0 token getter
 * @param {string|number} params.year - Year for the mission data
 * @param {string} params.format - Format of the data ('csv' or 'json')
 * @param {Function} params.getDynamicFilename - Optional function to generate filename
 * @returns {Promise<Object>} Response with download trigger
 */
export const fetchMissionsData = async ({
  getAccessTokenSilently,
  year,
  format = 'csv',
  getDynamicFilename
}) => {
  if (!year) {
    throw new Error('Year parameter is required');
  }

  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error('getAccessTokenSilently is not a function. Please check Auth0 integration.');
  }

  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  const resolvedFormat = String(format).toLowerCase();
  if (!['csv', 'json'].includes(resolvedFormat)) {
    throw new Error('Format must be either "csv" or "json"');
  }

  const apiUrl = new URL(`${FUNCTION_URL}/api/v1/datasets/missions`);
  apiUrl.searchParams.set('year', String(year));
  apiUrl.searchParams.set('format', resolvedFormat);
  addFunctionKeyToUrl(apiUrl);

  const response = await fetch(apiUrl.toString(), {
    method: 'GET',
    headers: buildKeyHeaders(accessToken),
  });

  const responseDataText = await response.text();

  if (!response.ok) {
    let errorDetailMessage = '';
    try {
      const errorJson = JSON.parse(responseDataText);
      errorDetailMessage =
        errorJson.error ||
        errorJson.message ||
        errorJson.detail ||
        responseDataText.substring(0, 200);
    } catch {
      errorDetailMessage = responseDataText.substring(0, 200);
    }
    throw new Error(`Error fetching missions data: ${response.status} - ${errorDetailMessage}`);
  }

  const contentTypeFromServer = response.headers.get('content-type');
  let processedResult;

  if (isJsonLike(contentTypeFromServer, responseDataText)) {
    processedResult = processJsonResponse(responseDataText);
    return triggerDownload(processedResult, getDynamicFilename, { 
      resolvedFormat: 'json', 
      selectedYear: year, 
      fallbackExt: 'json' 
    });
  }

  processedResult = processCsvResponse(responseDataText, 'v1/datasets/missions');
  return triggerDownload(processedResult, getDynamicFilename, { 
    resolvedFormat: 'csv', 
    selectedYear: year, 
    fallbackExt: 'csv' 
  });
};

/**
 * Fetches UN Secretariat volunteer data for a given year.
 * 
 * @param {Object} params
 * @param {Function} params.getAccessTokenSilently - Auth0 token getter
 * @param {string|number} params.year - Year for the UN Secretariat data
 * @param {string} params.format - Format of the data ('csv' or 'json')
 * @param {Function} params.getDynamicFilename - Optional function to generate filename
 * @returns {Promise<Object>} Response with download trigger
 */
export const fetchUNSecretariatData = async ({
  getAccessTokenSilently,
  year,
  format = 'csv',
  getDynamicFilename
}) => {
  if (!year) {
    throw new Error('Year parameter is required');
  }

  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error('getAccessTokenSilently is not a function. Please check Auth0 integration.');
  }

  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  const resolvedFormat = String(format).toLowerCase();
  if (!['csv', 'json'].includes(resolvedFormat)) {
    throw new Error('Format must be either "csv" or "json"');
  }

  const apiUrl = new URL(`${FUNCTION_URL}/api/v1/datasets/unsecretariat`);
  apiUrl.searchParams.set('year', String(year));
  apiUrl.searchParams.set('format', resolvedFormat);
  addFunctionKeyToUrl(apiUrl);

  const response = await fetch(apiUrl.toString(), {
    method: 'GET',
    headers: buildKeyHeaders(accessToken),
  });

  const responseDataText = await response.text();

  if (!response.ok) {
    let errorDetailMessage = '';
    try {
      const errorJson = JSON.parse(responseDataText);
      errorDetailMessage =
        errorJson.error ||
        errorJson.message ||
        errorJson.detail ||
        responseDataText.substring(0, 200);
    } catch {
      errorDetailMessage = responseDataText.substring(0, 200);
    }
    throw new Error(`Error fetching UN Secretariat data: ${response.status} - ${errorDetailMessage}`);
  }

  const contentTypeFromServer = response.headers.get('content-type');
  let processedResult;

  if (isJsonLike(contentTypeFromServer, responseDataText)) {
    processedResult = processJsonResponse(responseDataText);
    return triggerDownload(processedResult, getDynamicFilename, { 
      resolvedFormat: 'json', 
      selectedYear: year, 
      fallbackExt: 'json' 
    });
  }

  processedResult = processCsvResponse(responseDataText, 'v1/datasets/unsecretariat');
  return triggerDownload(processedResult, getDynamicFilename, { 
    resolvedFormat: 'csv', 
    selectedYear: year, 
    fallbackExt: 'csv' 
  });
};

/**
 * Fetches the DSA document (HTML or PDF) for a given user email.
 * This is used for previewing the DSA before acceptance (public endpoint).
 * 
 * @param {Object} params
 * @param {string} params.userEmail - User's email (required)
 * @param {string} params.format - Format: "html" or "pdf" (default: "html")
 * @returns {Promise<Object>} Response with contentType and body
 */
export const getDsaDocument = async ({
  userEmail,
  format = "html"
}) => {
  if (!userEmail) {
    throw new Error('User email parameter is required');
  }

  const url = new URL(`${FUNCTION_URL}/api/v1/dsa/document`);
  url.searchParams.set('email', userEmail);
  url.searchParams.set('format', format);
  addFunctionKeyToUrl(url);

  const res = await fetch(url.toString(), {
    method: 'GET',
  });

  const contentType = res.headers.get('content-type') || '';

  if (!res.ok) {
    const bodyText = await res.text();
    let detail = bodyText;
    try {
      const j = JSON.parse(bodyText);
      detail = j.error || j.message || j.detail || bodyText.slice(0, 200);
    } catch (_) {
      detail = bodyText.slice(0, 200);
    }
    throw new Error(`Error fetching DSA document: ${res.status} - ${detail}`);
  }

  if (format === "pdf" && contentType.includes("application/pdf")) {
    const blob = await res.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `DSA_${userEmail.replace(/[@.]/g, '_')}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
    return { success: true };
  }

  const bodyText = await res.text();
  return {
    contentType,
    body: bodyText,
    isHtml: contentType.includes('text/html')
  };
};

/**
 * Fetches the accepted DSA document for the authenticated user.
 * This returns the DSA that was already accepted and archived.
 * 
 * @param {Object} params
 * @param {Function} params.getAccessTokenSilently - Auth0 token getter
 * @param {string} params.format - Format: "md" or "pdf" (default: "pdf")
 * @returns {Promise<Object>} Response with the accepted DSA content
 */
export const getAcceptedDsaDocument = async ({ getAccessTokenSilently, format = "pdf" }) => {
  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error('getAccessTokenSilently is not a function. Please check Auth0 integration.');
  }

  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  const url = new URL(`${FUNCTION_URL}/api/v1/dsa/accepted-document`);
  url.searchParams.set('format', format);
  addFunctionKeyToUrl(url);

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: buildBearerHeaders(accessToken),
  });

  const contentType = res.headers.get('content-type') || '';

  if (!res.ok) {
    const bodyText = await res.text();
    let detail = bodyText;
    try {
      const j = JSON.parse(bodyText);
      detail = j.error || j.message || j.detail || bodyText.slice(0, 200);
    } catch (_) {
      detail = bodyText.slice(0, 200);
    }
    throw new Error(`Error fetching accepted DSA: ${res.status} - ${detail}`);
  }

  if (format === "pdf" && contentType.includes("application/pdf")) {
    const blob = await res.blob();
    return {
      contentType,
      body: blob,
      isPdf: true
    };
  }

  const bodyText = await res.text();
  return {
    contentType,
    body: bodyText,
    isPdf: false
  };
};

// === New function to obtain the user token ===
export const getUserTokenFromBackend = async (getAccessTokenSilently) => {
  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error('getAccessTokenSilently is not a function. Please check Auth0 integration.');
  }

  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });

  const url = new URL(`${FUNCTION_URL}/api/v1/users/token`);
  if (FUNCTION_KEY) url.searchParams.set('code', FUNCTION_KEY);

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });

  if (!res.ok) {
    throw new Error('Failed to fetch user token from backend');
  }
  return await res.json();
};

/**
 * PAT Management Services
 */
export const getPatInfo = async (getAccessTokenSilently) => {
  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });
  const url = addFunctionKeyToUrl(new URL(`${FUNCTION_URL}/api/v1/users/access-key`));
  const res = await fetch(url.toString(), {
    headers: buildBearerHeaders(accessToken),
  });
  return res.ok ? await res.json() : { exists: false };
};

export const createPat = async (getAccessTokenSilently, { expirationDays }) => {
  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });
  const url = addFunctionKeyToUrl(new URL(`${FUNCTION_URL}/api/v1/users/access-key`));
  const res = await fetch(url.toString(), {
    method: 'POST',
    headers: { ...buildBearerHeaders(accessToken), 'Content-Type': 'application/json' },
    body: JSON.stringify({ 
      expiryDays: parseInt(expirationDays)
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
};

export const revokePat = async (getAccessTokenSilently) => {
  const accessToken = await getAccessTokenSilently({
    authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
  });
  const url = addFunctionKeyToUrl(new URL(`${FUNCTION_URL}/api/v1/users/access-key`));
  const res = await fetch(url.toString(), {
    method: 'DELETE',
    headers: buildBearerHeaders(accessToken),
  });
  if (!res.ok) throw new Error(await res.text());
  return true;
};

export const fetchDatasetDocumentation = async ({
  getAccessTokenSilently,
  datasetId,
}) => {
  if (typeof getAccessTokenSilently !== "function") {
    throw new Error(
      "getAccessTokenSilently is not a function. Please check Auth0 integration."
    );
  }

  if (!datasetId) {
    throw new Error("Dataset identifier is required.");
  }

  const accessToken = await getAccessTokenSilently({
    authorizationParams: {
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    },
  });

  const encodedDatasetId = encodeURIComponent(datasetId);

  const url = new URL(
    `${FUNCTION_URL}/api/v1/datasets/documentation/${encodedDatasetId}`
  );

  addFunctionKeyToUrl(url);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: buildBearerHeaders(accessToken),
  });

  const responseText = await response.text();

  if (!response.ok) {
    let errorDetail = responseText.slice(0, 200);

    try {
      const errorJson = JSON.parse(responseText);
      errorDetail =
        errorJson.error ||
        errorJson.message ||
        errorJson.detail ||
        errorDetail;
    } catch {
      // Response is not JSON.
    }

    throw new Error(
      `Error fetching dataset documentation: ${response.status} - ${errorDetail}`
    );
  }

  return responseText;
};